import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import Button from '../../Button';
import Hyperlink from '../../Hyperlink';
import BlurReveal from '../../BlurReveal';
import PatchBurst, { PATCH_BURST_ITEM_MS } from '../../PatchBurst';
import './HeroBannerV3.css';

/**
 * HeroBannerV3 — split hero on a warm gradient: eyebrow, editorial headline
 * (with an accented tail), copy, a CTA Button + a Hyperlink and a stats row on the
 * left; a square visual (image, or a muted looping video with the image as
 * its poster) on the right.
 *
 * Figma (OVER-LL): Desktop `2217:151672` (1600×800, two equal columns inside
 * the layout gutters), Mobile `2645:13284` (visual first, 344 square, stats
 * hidden).
 *
 * - `title` + `titleAccent` are one `<h1>`; the accent is a `<span>` coloured
 *   `--color-brand-primary-alternative`, so the lines wrap naturally.
 * - `video` plays OVER the picture: the picture (`image` / `imageMobile`) is
 *   what the visitor sees until the video reports it can play (`canplay`, or
 *   `readyState` already there from cache), then the video fades in on top
 *   and the picture stays underneath as its fallback — a clip that never
 *   loads (slow network, blocked autoplay data, error) leaves the still.
 *   Author the still as the clip's first/last frame so the hand-over is
 *   invisible.
 * - `visualPosition` puts the visual at the `end` (right, default — Figma) or `start` (left) of the
 *   text on desktop; mobile always stacks the visual first.
 * - `stats` render desktop-only (Figma mobile has no stats row).
 * - `reveal` plays the DS BlurReveal entrance across the hero: eyebrow, then
 *   the headline word by word (`titleReveal`, accent span kept), then copy, buttons and
 *   stats as blocks, while the visual fades in alongside — in place, no lift;
 *   a video fades without the blur filter (`REVEAL_TIMING`).
 * - `burst` (with `reveal`) plays a PatchBurst over the text column while the
 *   headline resolves, timed to end with the text — positions in % of the
 *   column; see PatchBurst.
 *   Off by default.
 * - Every text row renders only when it has content.
 */
/**
 * Entrance score, ms after mount. ONE composition: the text rows land in
 * order, the last row (stats) lands at ~1.49s (`end`), and the patch burst is
 * scheduled BACKWARDS from `burstEnd` — whatever the burst config, the last
 * patch is gone by then. `burstEnd` = 64.8% of `end` on purpose: a glyph reads
 * as there at about a third of its run (ease-out), so the text READS sharp
 * before the last glyph tails fade to `end`; a patch lingering into that
 * window reads as late.
 */
const REVEAL_TIMING = {
  subtitle: 0,
  title: 96,
  visual: 192,
  copy: 480,
  ctas: 624,
  stats: 768,
  end: 1490,
};
REVEAL_TIMING.burstEnd = Math.round(REVEAL_TIMING.end * 0.648);
const VISUAL_DURATION = 960;

/**
 * Headline entrances (`titleReveal`). `words` is the one chosen in the
 * motion review (2026-10-08) and the default; the others stay available:
 * words — fade + small-l lift per word, 38ms apart, 480ms, UI ease-out, the default 2px blur;
 * glyphs — the per-character blur reveal (on the spring, as reviewed);
 * lines — each authored chunk rises out of a baseline mask, 90ms apart;
 * rows — the headline arrives as one block like the other rows;
 * glyphs-plain — the character sweep without blur or spring, 12ms apart.
 */
const TITLE_REVEALS = {
  glyphs: { easing: 'spring' },
  words: {
    unit: 'word',
    stagger: 38,
    duration: 480,
    distance: 'var(--space-ui-small-l)',
  },
  lines: { variant: 'lines', stagger: 90, duration: 480 },
  'glyphs-plain': { blur: false, stagger: 12, duration: 300, distance: 'var(--space-ui-small-s)' },
};

/** Scene offset that makes the burst's last patch end at REVEAL_TIMING.burstEnd */
const burstDelayFor = (items) => {
  const last = Math.max(0, ...items.map((i) => i.delay || 0)) + PATCH_BURST_ITEM_MS;
  return Math.max(0, REVEAL_TIMING.burstEnd - last);
};

/* A hero row: plain tag, or a BlurReveal block when the hero reveals */
const Row = ({ reveal, delay, duration, lift, blur, as: Tag = 'div', className, children }) =>
  reveal ? (
    <BlurReveal variant="block" as={Tag} delay={delay} duration={duration} lift={lift} blur={blur} className={className}>
      {children}
    </BlurReveal>
  ) : (
    <Tag className={className}>{children}</Tag>
  );

Row.propTypes = {
  reveal: PropTypes.bool,
  delay: PropTypes.number,
  duration: PropTypes.number,
  lift: PropTypes.bool,
  blur: PropTypes.bool,
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node,
};

const HeroBannerV3 = ({
  subtitle = '',
  title = '',
  titleAccent = '',
  copy = '',

  ctaLabel = '',
  ctaIcon = <ArrowRight />,
  ctaVariant = 'main',
  ctaClassName = '',
  ctaHref,
  ctaOnClick,

  secondaryLabel = '',
  secondaryHref = '',
  secondaryOnClick,

  stats = [],
  reveal = false,
  titleReveal = 'words',
  burst = [],

  image,
  imageMobile,
  imageAlt = '',
  video,
  visualPosition = 'end',
  background,
  className = '',
}) => {
  const hasVisual = Boolean(video || image);
  // 'start' / 'end' is the DS name shared with ContentBanner / ContentBannerV2;
  // 'left' / 'right' are kept as aliases
  const visualFirst = visualPosition === 'start' || visualPosition === 'left';

  // The video shows only once it can play; until then (and if it never can)
  // the picture is the visual. A cached clip may be ready before React
  // attaches the listener, so the mount effect reads readyState too.
  const videoRef = useRef(null);

  // Mobile sizing: the visual takes whatever the screen leaves above the text
  // block (see HeroBannerV3.css), so the text block's height is measured into
  // `--hero-v3-content-h`. Padding included (offsetHeight).
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setContentHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The screen the band fills (`--hero-v3-screen-h`). CSS `100svh` alone is
  // not enough: Chrome on iOS reports it ~130px short of what it shows (its
  // toolbars counted twice), which dropped the hero to its floor and pulled
  // the next section into the first screen. The CSS takes the larger of the
  // two. Read on mount and when the WIDTH changes (rotation) only — height-only
  // resizes are toolbars collapsing or a keyboard, and the hero must not
  // resize mid-scroll.
  const [screenHeight, setScreenHeight] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let width = window.innerWidth;
    setScreenHeight(window.innerHeight);
    const onResize = () => {
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      setScreenHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [videoReady, setVideoReady] = useState(false);
  const markVideoReady = () => setVideoReady(true);
  useEffect(() => {
    setVideoReady(false);
    const el = videoRef.current;
    if (el && el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) setVideoReady(true);
  }, [video]);
  const titleContent = (
    <>
      {title}
      {titleAccent && (
        <>
          {title ? ' ' : ''}
          <span className="hero-v3__title-accent">{titleAccent}</span>
        </>
      )}
    </>
  );

  // Hyperlink defaults to href="#" — don't let a click-only link touch the URL.
  const handleSecondaryClick = (e) => {
    if (!secondaryHref) e.preventDefault();
    secondaryOnClick?.(e);
  };

  return (
    <section
      className={`hero-v3${visualFirst ? ' hero-v3--visual-left' : ''}${className ? ` ${className}` : ''}`}
      style={{
        ...(background ? { '--hero-v3-bg': background } : null),
        ...(contentHeight ? { '--hero-v3-content-h': `${contentHeight}px` } : null),
        ...(screenHeight ? { '--hero-v3-screen-h': `${screenHeight}px` } : null),
      }}
    >
      <div className="hero-v3__container">
        <div className="hero-v3__content" ref={contentRef}>
          {reveal && burst.length > 0 && <PatchBurst items={burst} delay={burstDelayFor(burst)} />}
          <div className="hero-v3__wrapper">
            {subtitle && (
              <Row reveal={reveal} delay={REVEAL_TIMING.subtitle} as="p" className="hero-v3__subtitle typo-body">
                {subtitle}
              </Row>
            )}

            {(title || titleAccent) && (reveal ? (
              titleReveal === 'rows' ? (
                <Row reveal delay={REVEAL_TIMING.title} as="h1" className="hero-v3__title typo-h1-editorial">
                  {titleContent}
                </Row>
              ) : (
                <BlurReveal
                  as="h1"
                  delay={REVEAL_TIMING.title}
                  className="hero-v3__title typo-h1-editorial"
                  {...TITLE_REVEALS[titleReveal]}
                >
                  {titleContent}
                </BlurReveal>
              )
            ) : (
              <h1 className="hero-v3__title typo-h1-editorial">{titleContent}</h1>
            ))}

            {copy && (
              <Row reveal={reveal} delay={REVEAL_TIMING.copy} as="p" className="hero-v3__copy typo-body-lg">
                {copy}
              </Row>
            )}

            {(ctaLabel || secondaryLabel) && (
              <Row reveal={reveal} delay={REVEAL_TIMING.ctas} className="hero-v3__cta-row">
                {ctaLabel && (
                  <Button
                    size="secondary"
                    variant={ctaVariant}
                    className={ctaClassName}
                    iconRight={ctaIcon}
                    href={ctaHref}
                    onClick={ctaOnClick}
                  >
                    {ctaLabel}
                  </Button>
                )}
                {ctaLabel && secondaryLabel && (
                  <span className="hero-v3__cta-divider typo-body" aria-hidden="true">/</span>
                )}
                {secondaryLabel && (
                  <Hyperlink
                    variant="regular"
                    href={secondaryHref || '#'}
                    onClick={handleSecondaryClick}
                    className="hero-v3__link"
                  >
                    {secondaryLabel}
                  </Hyperlink>
                )}
              </Row>
            )}

            {stats.length > 0 && (
              <Row reveal={reveal} delay={REVEAL_TIMING.stats} as="dl" className="hero-v3__stats">
                {stats.map(({ label, value }) => (
                  <div key={label} className="hero-v3__stat">
                    <dt className="hero-v3__stat-label typo-body-sm">{label}</dt>
                    <dd className="hero-v3__stat-value typo-body-editorial">{value}</dd>
                  </div>
                ))}
              </Row>
            )}
          </div>
        </div>

        {hasVisual && (
          <Row reveal={reveal} delay={REVEAL_TIMING.visual} duration={VISUAL_DURATION} lift={false} blur={!video} className="hero-v3__visual">
            <div className={`hero-v3__frame${video ? ' hero-v3__frame--video' : ''}${videoReady ? ' hero-v3__frame--playing' : ''}`}>
              {image && (
                <picture className="hero-v3__picture">
                  <source media="(min-width: 1024px)" srcSet={image} />
                  <img className="hero-v3__media" src={imageMobile || image} alt={imageAlt} />
                </picture>
              )}
              {video && (
                <video
                  ref={videoRef}
                  className="hero-v3__media hero-v3__video"
                  src={video}
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="auto"
                  onCanPlay={markVideoReady}
                  aria-label={!image ? imageAlt || undefined : undefined}
                  aria-hidden={image ? 'true' : undefined}
                />
              )}
            </div>
          </Row>
        )}
      </div>
    </section>
  );
};

HeroBannerV3.propTypes = {
  /** Eyebrow above the headline (24px rule + uppercase text) */
  subtitle: PropTypes.string,
  /** Headline (typo-h1-editorial) */
  title: PropTypes.string,
  /** Tail of the headline rendered in --color-brand-primary-alternative */
  titleAccent: PropTypes.string,
  /** Body copy under the headline */
  copy: PropTypes.string,
  /** Primary CTA label (Button secondary, `ctaVariant`) */
  ctaLabel: PropTypes.string,
  /** Primary CTA icon */
  ctaIcon: PropTypes.node,
  /** Primary CTA Button variant */
  ctaVariant: PropTypes.string,
  /** Extra class on the primary CTA (placement hooks; a skin is a `ctaVariant`, e.g. `hero`) */
  /** Primary CTA link target — renders the CTA as a real link */
  ctaHref: PropTypes.string,
  ctaClassName: PropTypes.string,
  /** Primary CTA handler */
  ctaOnClick: PropTypes.func,
  /** Secondary link label (DS Hyperlink regular, next to the CTA) */
  secondaryLabel: PropTypes.string,
  /** Secondary link href; omitted = click-only (no URL change) */
  secondaryHref: PropTypes.string,
  /** Secondary link handler */
  secondaryOnClick: PropTypes.func,
  /** Stats row under the buttons — desktop only. [{ label, value }] */
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  /** Play the BlurReveal entrance across the hero (eyebrow → headline glyphs → copy → buttons → stats; visual alongside) */
  reveal: PropTypes.bool,
  /** How the headline enters when `reveal` is on: words (default) | glyphs (blur reveal) | lines | rows | glyphs-plain — see TITLE_REVEALS */
  titleReveal: PropTypes.oneOf(['glyphs', 'words', 'lines', 'rows', 'glyphs-plain']),
  /** PatchBurst scene over the text column during the reveal (PatchBurst `items`; needs `reveal`) */
  burst: PropTypes.array,
  /** Desktop visual; with `video` it is the still shown until the clip can play (author it as the clip's first/last frame) */
  image: PropTypes.string,
  /** Mobile visual (falls back to image) */
  imageMobile: PropTypes.string,
  /** Alt text for the visual */
  imageAlt: PropTypes.string,
  /** Video URL — muted autoplay loop that fades in over the picture once it can play */
  video: PropTypes.string,
  /** Desktop side of the visual: right (default) | left. Mobile always shows it first */
  visualPosition: PropTypes.oneOf(['end', 'start', 'right', 'left']),
  /** Override of the section background (any CSS background value) */
  background: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default HeroBannerV3;
